/**
 * Observe the page to load or destroy component instances.
 * This supports both synchronous and asynchronous component lazy loading.
 *
 * If Barba is enabled, components will also be managed throughout Barba's lifecycles.
 */
export class ClientComponentManager {
  /**
   * @param {function|string} component - component class, or file handle for async loading.
   * @param {string} selector - selector to determine if page needs component.
   */
  constructor(component, selector) {
    this.component = component;
    this.selector = selector;

    this.instances = []; // Track component instances that will be destroyed.
    this.loadedDataAttribute = `data-loaded-${typeof component === "string" ? component : component.name}`;

    this.load();

    /**
     * Barba lifecycle hooks to manage components on page transitions.
     * Load `barba` and uncomment the following if Barba is enabled.
     */
    // barba.hooks.after(() => this.load());
    // barba.hooks.beforeLeave(() => this.destroy());
  }

  load = async () => {
    // Get all matching elements that are not already loaded.
    const elements = document.querySelectorAll(
      `${this.selector}:not([${this.loadedDataAttribute}])`,
    );

    if (elements.length === 0) {
      return;
    }

    // If component is a handle for a component, dynamically import it.
    if (typeof this.component === "string") {
      const module = await import(`../components/${this.component}.js`);
      this.component = module.default;
    }

    elements.forEach((element) => {
      element.setAttribute(this.loadedDataAttribute, "");
      const instance = new this.component(element);

      this.instances.push(instance);
    });
  };

  destroy = async () => {
    this.instances.forEach((instance) => {
      if (typeof instance.disconnect === "function") {
        instance.disconnect();
      }
    });

    this.instances = [];
  };
}
